<template>
  <div>
    <NotAvailableMessage
      v-if="notAvailableType"
      :not-available-type="notAvailableType"
      :company-uuid="companyUuid"
    />
    <div v-else class="p-box">
      <!-- ヘッダー -->
      <div class="p-box__header">
        <div class="p-filtersA">
          <div v-if="!analysisAxesLoading" class="p-filtersA__left">
            <Select v-model="currentAnalysisTypeId" :options="analysisTypeOptions" />
            <p class="plus">
              ×
            </p>
            <Select v-model="dataTypeId" :options="dataTypeOptions" />
            <div v-if="!searchAttributesLoading" class="c-buttonFilter" @click="modal = true">
              {{ $t('pages.CompaniesReviews.filters') }}
              <div class="c-buttonFilter__icon">
                <Icon name="iconFilter" />
              </div>
            </div>
          </div>
          <div class="p-filtersA__right">
            <DatePicker
              v-if="period"
              :initial-dates="period"
              opens="left"
              @input="changePeriod"
            />
            <div v-if="canExportAuthority('analysis/rankings') && providedFeaturesCsvExport">
              <KkButton
                class="csvButton"
                color="secondary"
                height="38px"
                :font-bold="false"
                :text="$t('labels.csvDownload')"
                @click.prevent="setExportCsv"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- テーブル -->
      <div class="p-table">
        <Loading v-show="loading" />
        <div v-show="!loading" class="p-table__content">
          <div v-if="items.length === 0" class="noData" v-text="$t('messages.noData')" />
          <div v-else class="p-box__body">
            <div class="analysis-table">
              <table>
                <thead>
                  <tr>
                    <th class="ranking-th">
                      {{ currentAnalysisType.name }}
                    </th>
                    <th class="ranking-th">
                      <button
                        class="p-table__content__sort"
                        :class="orderCss"
                        @click="sort()"
                      >
                        {{ dataType.name }}
                      </button>
                    </th>
                  </tr>
                  <tr class="p-table__content__space">
                    <td colspan="2" />
                  </tr>
                </thead>
                <tbody v-show="showTableBody" ref="analysisTableBody">
                  <tr class="average-chart-wrapper" :style="{ left: halfTableBodyWidth + 'px' }">
                    <p :style="{ marginLeft: averageChartValue - 43 + 'px' }">{{ $t('pages.companies.analysis.ranking.averageValue', {value: numberLocaleString(averageValue) }) }}</p>
                    <AverageChart :value="averageChartValue" :height-value="tableBodyHeight - 23" :width-value="halfTableBodyWidth" />
                  </tr>
                  <tr>
                    <td class="table-head" style="color: #F8F9FA;">-</td>
                    <td>
                      <div class="min-max-values">
                        <span>{{ numberLocaleString(minValue) }}</span>
                        <span>{{ numberLocaleString(maxValue) }}</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-for="(item, itemIndex) in itemList" :key="itemIndex">
                    <td class="table-head">
                      <span class="rankingNumber">{{ item.rankNumber }}. </span>  {{ item.name }}
                    </td>
                    <td class="chart-td-wrapper">
                      <div class="chart-td">
                        <Chart
                          :value="convertChartValue(extractItemData(item))"
                          :color="chartColor(extractItemData(item))"
                        />
                        <span class="chart-value">{{ numberLocaleString(extractItemData(item)) }}</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div v-if="totalPages" class="p-controller">
        <div class="p-controller__count">
          <p class="p-controller__count__name">
            {{ $t('labels.perPageCount') }}
          </p>
          <div class="p-controller__count__select">
            <select :value="displayNumber" @change="changeDisplayNumber($event)">
              <option v-for="item in 5" :key="item" :value="item * 50">{{ item * 50 }}</option>
            </select>
          </div>
          <p class="p-controller__count__current">{{ startDataNumber }}-{{ endDataNumber }}</p>
        </div>
        <div class="p-controller__navi">
          <button
            type="button"
            :disabled="loading || currentPage === 1"
            class="p-controller__navi__prev"
            @click="goPrevPage"
          >
            <Icon name="arrowLeft" />
          </button>
          <button
            type="button"
            :disabled="loading || currentPage >= totalPages"
            class="p-controller__navi__next"
            @click="goNextPage"
          >
            <Icon name="arrowRight" />
          </button>
        </div>
      </div>
      <KkFilterModal
        :is-visible="modal"
        :filter-items="filterItems"
        :form-values="formValues"
        :total-count="filteredCount"
        @close="modal = false"
        @submit="handleSubmit"
        @reset="handleReset"
        @change-check-box="handleChangeFilterModalCheckBox"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import moment from 'moment'
import { toNumberLocaleString } from '../../../modules/localeString'
import { reviewDataTypes } from '../../../modules/analysis'
import Select from '../../../components/Select'
import DatePicker from '../../../components/DatePicker'
import { KkFilterModal } from '../../../components/projects'
import Chart from '../../../components/CompaniesAnalysis/Ranking/Chart'
import AverageChart from '../../../components/CompaniesAnalysis/Ranking/AverageChart'
import companiesNotAvailableTypeMixin from '../../../mixins/companiesNotAvailableTypeMixin'
import NotAvailableMessage from '../../../components/NotAvailableMessage'
import Loading from '../../../components/Loading'
import Icon from '../../../components/Icon'
import { addAttributes } from '../../../modules/filterModalConst'
import { KkButton } from '../../../components/base'
import { actionEvent } from 'src/biz_spa/modules/googleAnalytics'

export default {
  name: 'CompaniesAnalysisRanking',
  components: {
    Chart,
    KkFilterModal,
    Select,
    DatePicker,
    Loading,
    AverageChart,
    NotAvailableMessage,
    Icon,
    KkButton
  },
  mixins: [ companiesNotAvailableTypeMixin ],
  beforeRouteUpdate (to, _, next) {
    this.initTableAttributesAndSubmit()
    next()
  },
  data() {
    return {
      currentAnalysisTypeId: 'store',  // 分析種別の ID
      dataTypeId: reviewDataTypes[0].id,                // 集計種別の ID
      modal: false,                                     // モーダルウィンドウを表示するかどうか
      sortOrder: 'desc',                                // テーブルの昇順, 降順
      halfTableBodyWidth: 0,                            // 平均値 canvas 要素の幅
      tableBodyHeight: 0,                               // 平均値 canvas 要素の高さ
      currentPage: 1,
      evacuationInitialFormValues: null,
      filterItems: [],
    }
  },
  computed: {
    ...mapState({
      // NOTE: 使用に制限のある軸があるため、computedに定義したanalysisAxesの方を利用する
      allAxes: state => state.companiesAnalysisAxes.data,
      analysisAxesLoading: state => state.companiesAnalysisAxes.loading,
      searchAttributesLoading: state => state.companiesSearchTagGroups.loading,
      formValues: state => state.companiesSearchTagGroups.formValues,
      loading: state => state.companiesAnalysisRanking.loading,
      items: state => state.companiesAnalysisRanking.data,
      filteredCount: state => state.companiesAnalysisRanking.filteredCount,
      searchAttributes: state => state.companiesSearchTagGroups.data,
      meta: state => state.companiesAnalysisRanking.meta,
      displayNumber: state => state.filters.displayNumber,
      period: state => state.filters.period,
    }),
    ...mapGetters({
      'canExportAuthority': 'companiesAccessibleApi/canExportAuthority',
      'providedFeaturesCsvExport': 'companiesCompany/providedFeaturesCsvExport',
      'inboundOption': 'companiesCompany/inboundOption',
    }),
    analysisAxes() {
      return this.allAxes.filter((axis) => {
        // NOTE: インバウンドオプションが有効の場合のみ、言語別が利用可能
        if (axis.id === 'locale') return this.inboundOption

        return true
      })
    },
    crossSearchField() {
      return null
    },
    pageType() {
      return null
    },
    totalPages() {
      return this.meta ? this.meta.totalPages : 0
    },
    startDataNumber() {
      return (this.currentPage - 1) * this.displayNumber + 1
    },
    endDataNumber() {
      return this.startDataNumber + this.items.length - 1
    },
    sortKey() {
      return this.dataType.id === 'scores' ? 'average_score' : 'posted_count'
    },
    companyUuid() {
      return this.$route.params.company_uuid
    },
    showTableBody() {
      return this.loading === false && this.items.length !== 0
    },
    averageValue() {
      if (!this.meta) return 0
      switch (this.dataTypeId) {
      case reviewDataTypes[0].id:
        return this.meta.totalAverageScore
      default:
        return this.meta.totalAveragePostedCount
      }
    },
    averageChartValue() {
      return this.convertChartValue(this.averageValue)
    },
    minValue() {
      return this.dataType.id === 'scores' ? this.meta.minAverageScore : this.meta.minPostedCount
    },
    maxValue() {
      return this.dataType.id === 'scores' ? this.meta.maxAverageScore : this.meta.maxPostedCount
    },
    dataType() {
      return reviewDataTypes.find(v => v.id === this.dataTypeId)
    },
    currentAnalysisType() {
      return this.analysisAxes.find(v => v.id === this.currentAnalysisTypeId)
    },
    isTotalCounts() {
      return this.dataTypeId === reviewDataTypes[2].id
    },
    searchPeriod() {
      return moment(this.period.startDate).format('YYYY/MM/DD')
        + ' - '
        + moment(this.period.endDate).format('YYYY/MM/DD')
    },
    analysisTypeOptions() {
      const obj = {}
      this.analysisAxes.forEach(v => obj[v.name] = v.id)
      return obj
    },
    dataTypeOptions() {
      const obj = {}
      reviewDataTypes.forEach(v => obj[v.text] = v.id)
      return obj
    },
    orderCss() {
      if (!this.sortOrder) return '' // 初期値の場合は CSS なし
      return this.sortOrder === 'asc' ? 'is-asc' : 'is-desc'
    },
    itemList() {
      let items = this.items

      switch (this.sortOrder) {
      case 'asc':
        items = items.map((v,i) => {
          return {
            ...v,
            rankNumber: this.meta?.totalCount - (this.startDataNumber + i) + 1
          }
        })
        break
      case 'desc':
        items = items.map((v,i) => {
          return {
            ...v,
            rankNumber: this.startDataNumber + i
          }
        })
        break
      }

      return items
    },
    query() {
      const obj = {
        site_types: this.formValues.sites.length === 0 ? this.evacuationInitialFormValues.sites : this.formValues.sites,
        store_uuids: this.formValues.stores.length === 0 ? this.evacuationInitialFormValues.stores : this.formValues.stores,
        brand_ids: this.formValues.brands.length === 0 ? this.evacuationInitialFormValues.brands : this.formValues.brands,
        franchisee_ids: this.formValues.franchisees.length === 0 ? this.evacuationInitialFormValues.franchisees : this.formValues.franchisees,
        area_ids: this.formValues.areas.length === 0 ? this.evacuationInitialFormValues.areas : this.formValues.areas,
        user_company_ids: this.formValues.user_companies.length === 0 ? this.evacuationInitialFormValues.user_companies : this.formValues.user_companies,
        prefectures: this.formValues.prefectures.length === 0 ? this.evacuationInitialFormValues.prefectures : this.formValues.prefectures,
        locales: this.formValues.locales,
        original_tag_group_a_tag_ids: this.formValues.original_tag_group_a,
        original_tag_group_b_tag_ids: this.formValues.original_tag_group_b,
        original_tag_group_c_tag_ids: this.formValues.original_tag_group_c,
        original_tag_group_d_tag_ids: this.formValues.original_tag_group_d,
        original_tag_group_e_tag_ids: this.formValues.original_tag_group_e,
        evaluation_axis: this.currentAnalysisType.fields,
        per: this.displayNumber,
        sort_order: this.sortOrder,
        sort_key: this.sortKey,
        page: this.currentPage,
        posted_at_term: this.searchPeriod,
        total_count: this.isTotalCounts,
      }
      if (this.isTotalCounts) {
        obj.total_count = true
      }
      return obj
    },
  },
  watch: {
    loading() {
      this.$nextTick(() => {
        this.setDomData()
      })
    },
    searchDateRange() {
      this.initTableAttributesAndSubmit()
    },
    currentAnalysisTypeId() {
      this.initTableAttributesAndSubmit()
    },
    dataTypeId() {
      this.initTableAttributesAndSubmit()
    },
  },
  async created() {
    await Promise.all([
      this.readCompaniesAnalysisAxes(),
      this.readSearchAttributes(this.$route.params.company_uuid)
    ])
    await this.setEvacuationInitialFormValues()
    this.setFormValues(this.formInitialValues())
    this.createFilter()
    this.readRankingData()
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    ...mapActions({
      readSearchAttributes: 'companiesSearchTagGroups/readSearchTagGroups',
      readAnalysisRanking: 'companiesAnalysisRanking/readAnalysisRanking',
      readAnalysisRankingForFilter: 'companiesAnalysisRanking/readAnalysisRankingForFilter',
      setDisplayNumber: 'filters/setDisplayNumber',
      setPeriod: 'filters/setPeriod',
      exportCsv: 'companiesAnalysisRanking/exportCsv',
      readCompaniesAnalysisAxes: 'companiesAnalysisAxes/readCompaniesAnalysisAxes'
    }),
    ...mapMutations({
      setFormValues: 'companiesSearchTagGroups/setFormValues',
    }),
    changeDisplayNumber(e) {
      this.setDisplayNumber(e.target.value)
    },
    /**
     * リサイズ時に平均値 canvas 要素の幅, 高さを再設定
     * @return { Object }
     */
    handleResize() {
      this.setDomData()
    },
    goPrevPage() {
      --this.currentPage
      this.paginate()
    },
    goNextPage() {
      ++this.currentPage
      this.paginate()
    },
    /**
     * 絞り込み条件の変更
     * @return { Void }
     */
    handleSubmit() {
      this.initTableAttributesAndSubmit()
      this.modal = false
    },
    /**
     * テーブルの属性(ページ番号,ソート)を初期化してクエリをsubmitする
     */
    initTableAttributesAndSubmit() {
      this.currentPage = 1
      this.sortOrder = 'desc'
      this.readRankingData()
    },
    sort() {
      if (this.loading) return

      this.sortOrder = (this.sortOrder === 'desc' ) ? 'asc' : 'desc'

      this.currentPage = 1
      this.readRankingData()
    },
    paginate() {
      window.scrollTo({ top: 0 })
      this.readRankingData()
    },
    /**
     * 平均値 canvas 要素の幅, 高さを設定
     * @return { Object }
     */
    setDomData() {
      this.halfTableBodyWidth = this.$refs.analysisTableBody?.clientWidth / 2
      this.tableBodyHeight = this.$refs.analysisTableBody?.clientHeight
    },
    /**
     * 検索条件の初期値
     * どこかでshallow copyされて挙動がおかしかったのでmethodに持ってきた
     * @return { Object }
     */
    formInitialValues() {
      return {
        stores: [],
        sites: [],
        brands: [],
        franchisees: [],
        areas: [],
        user_companies: [],
        prefectures: [],
        locales: [],
        original_tag_group_a: [],
        original_tag_group_b: [],
        original_tag_group_c: [],
        original_tag_group_d: [],
        original_tag_group_e: []
      }
    },
    /**
     * itemListのitemから数値（データ）を抽出する
     * @param { Object } item
     * @return { Number }
     */
    extractItemData(item) {
      if (this.isTotalCounts) {
        return item[reviewDataTypes[1].id]
      }
      return item[this.dataTypeId]
    },
    /**
     * グラフライブラリに渡す色の値
     * @param  { Number } value
     * @return { String }
     */
    chartColor(value) {
      return value >= this.averageValue ? '#4C959A' : '#E9446A'
    },
    /**
     * グラフライブラリに渡す数値
     * @param  { Number } value
     * @return { Number }
     */
    convertChartValue(value) {
      if (value === 0) return 0
      if (this.minValue === this.maxValue) {
        const unit = this.halfTableBodyWidth * 0.875 / this.maxValue
        return value * unit + 2
      }
      const unit = this.halfTableBodyWidth * 0.875 / (this.maxValue - this.minValue)
      return (value - this.minValue) * unit + 2
    },
    /**
     * 絞り込み条件のリセット
     * @return { Void }
     */
    handleReset() {
      this.setFormValues(this.formInitialValues())
      this.initTableAttributesAndSubmit()
      this.modal = false
    },
    /**
     * テーブルの降順, 昇順を変更
     * @return { Void }
     */
    sortTables() {
      this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc'
    },
    /**
     * 数値のローカライズ
     * @param  { Number } value
     * @return { String }
     */
    numberLocaleString(value) {
      return toNumberLocaleString(value)
    },
    /**
     * 期間の変更
     * @param  { Object } value
     * @return { Void }
     */
    changePeriod(value) {
      this.setPeriod(value)
      this.readRankingData()
    },
    /**
     * ランキングデータを取得
     * @return { Void }
     */
    readRankingData() {
      this.readAnalysisRanking({
        companyUuid: this.$route.params.company_uuid,
        query: this.query,
      })
    },
    async setExportCsv() {
      const isSuccess = await this.exportCsv({
        companyUuid: this.companyUuid,
        query: this.query
      })
      if (!isSuccess) return
      this.$router.push({ name: 'CompaniesExportResults' })
      actionEvent('CsvDownload')
    },
    handleChangeFilterModalCheckBox({ kind, id }) {
      const newFormValues = Object.assign({}, this.formValues)

      if(newFormValues[kind].includes(id)) {
        newFormValues[kind] = newFormValues[kind].filter((_id) => _id !== id)
      } else {
        newFormValues[kind].push(id)
      }

      this.setFormValues(newFormValues)

      this.readAnalysisRankingForFilter({
        companyUuid: this.$route.params.company_uuid,
        query: this.query,
      })
    },
    async setEvacuationInitialFormValues() {
      this.evacuationInitialFormValues = {
        stores: this.searchAttributes.stores.tags.map(store => store.id),
        sites: this.searchAttributes.sites.tags.map(v => v.id),
        brands: this.searchAttributes.brands.tags.map(v => v.id),
        franchisees: this.searchAttributes.franchisees.tags.map(v => v.id),
        areas: this.searchAttributes.areas.tags.map(v => v.id),
        user_companies: this.searchAttributes.user_companies.tags.map(v => v.id),
        prefectures: this.searchAttributes.prefectures.tags.map(v => v.id),
        locales: this.inboundOption ? [this.searchAttributes.locales.tags.map(v => v.id)] : [],
        original_tag_group_a: this.searchAttributes.original_tag_group_a?.tags.map(v => v.id),
        original_tag_group_b: this.searchAttributes.original_tag_group_b?.tags.map(v => v.id),
        original_tag_group_c: this.searchAttributes.original_tag_group_c?.tags.map(v => v.id),
        original_tag_group_d: this.searchAttributes.original_tag_group_d?.tags.map(v => v.id),
        original_tag_group_e: this.searchAttributes.original_tag_group_e?.tags.map(v => v.id)
      }
    },
    createFilter() {
      const attributes = [
        this.searchAttributes.sites,
        this.searchAttributes.brands,
        this.searchAttributes.areas,
        this.searchAttributes.franchisees,
        this.searchAttributes.user_companies,
      ]

      if(this.inboundOption) attributes.unshift(this.searchAttributes.locales)

      if(this.searchAttributes.original_tag_group_a) attributes.push(this.searchAttributes.original_tag_group_a)
      if(this.searchAttributes.original_tag_group_b) attributes.push(this.searchAttributes.original_tag_group_b)
      if(this.searchAttributes.original_tag_group_c) attributes.push(this.searchAttributes.original_tag_group_c)
      if(this.searchAttributes.original_tag_group_d) attributes.push(this.searchAttributes.original_tag_group_d)
      if(this.searchAttributes.original_tag_group_e) attributes.push(this.searchAttributes.original_tag_group_e)

      attributes.push(this.searchAttributes.prefectures)
      attributes.push(this.searchAttributes.stores)

      this.filterItems = attributes.map((attr) => {
        if(attr && attr.kind){
          return addAttributes(attr, attr.kind)
        }
      })
    }
  },
}
</script>

<style lang="scss" scoped>
@import "../../../../../stylesheets/biz_spa/foundation/_var.scss";
@import "../../../../../stylesheets/biz_spa/foundation/_mixin.scss";
.plus {
  margin: 16px auto 0;
  @include mq(sm) {
    margin: auto 0 auto 16px;
  }
}
.p-filtersA__right {
  margin-left: auto;
}
.analysis-table {
  overflow-y: auto;
  max-height: 84vh;
  table {
    position: relative;
    z-index: 0;
  }
  thead {
    position: sticky;
    top: 0;
    z-index: 2;
  }
  tbody {
    position: relative;
  }
}
.average-chart-wrapper {
  position: absolute;
  color: #E9446A;
}
tbody .table-head {
  background-color: #F8F9FA;
}
tr th {
  width: 50%;
}
.chart-td-wrapper {
  padding-left: 0;
}
.chart-td {
  display: flex;
  .chart-value {
    margin-left: 16px;
    font-weight: 700;
  }
  canvas {
    height: 16px;
    margin: auto 0;
  }
}
.min-max-values {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}
.noData {
  margin: 0 32px 32px;
}
.rankingNumber {
  font-weight: bold;
  margin-right: 8px;
}
.csvButton {
  margin-left: 10px;
}
</style>
